import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import API from '../services/api';
import { Dropdown, Badge, Button, Offcanvas, Modal, Form, Spinner } from 'react-bootstrap';
import { BsBellFill, BsList } from 'react-icons/bs';
import './AdminResponsive.css';
import './AdminBottomNav.css';
import { resolveProfilePhoto } from "../utils/profilePhoto";
import {
  clearStoredSession,
  getStoredUser,
  redirectToLogin,
  SESSION_USER_UPDATED_EVENT,
  updateStoredUser,
} from "../utils/sessionUser";
import { clearAdminPushSession, initAdminPush } from "../utils/push";

const GST_API_LIFETIME_MESSAGE = "Your GST API key is activated for lifetime.";

const AdminMainLayout = () => {
  const [eligible, setEligible] = useState({ vendors: [], employees: [] });
  const [notificationCount, setNotificationCount] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(false); // mobile sidebar
  const [user, setUser] = useState(() => getStoredUser());
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [waLoading, setWaLoading] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [waError, setWaError] = useState("");
  const [waTemplates, setWaTemplates] = useState([]);
  const [showGstApiDuePopup, setShowGstApiDuePopup] = useState(true);
  const [waForm, setWaForm] = useState({
    to: "",
    recipientName: "",
    templateId: "",
  });
  const location = useLocation();
  const navigate = useNavigate();
  const handleGoBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/admin');
  };

  const matchesRoutePattern = (pattern, pathname) => {
    if (!pattern || !pathname) return false;
    if (!pattern.includes(":")) {
      return pathname === pattern || pathname.startsWith(`${pattern}/`);
    }
    const patternParts = pattern.split("/").filter(Boolean);
    const pathParts = pathname.split("/").filter(Boolean);
    if (pathParts.length < patternParts.length) return false;
    return patternParts.every((part, index) => part.startsWith(":") || part === pathParts[index]);
  };

  useEffect(() => {
    const fetchEligible = async () => {
      try {
        const res = await API.get('/admin/eligible-certificates');
        setEligible(res.data);
      } catch (err) {
        console.error('Error fetching eligibility list', err);
      }
    };

    const fetchNotificationCount = async () => {
      try {
        const res = await API.get('/admin/notifications/unread-count');
        setNotificationCount(Number(res.data?.count || 0));
      } catch (err) {
        console.error('Error fetching admin notifications:', err);
      }
    };

    fetchEligible();
    fetchNotificationCount();
    const intervalId = window.setInterval(fetchNotificationCount, 15000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const syncUser = () => setUser(getStoredUser());
    window.addEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
    window.addEventListener("storage", syncUser);
    return () => {
      window.removeEventListener(SESSION_USER_UPDATED_EVENT, syncUser);
      window.removeEventListener("storage", syncUser);
    };
  }, []);

  useEffect(() => {
    const syncAdminProfile = async () => {
      try {
        const { data } = await API.get('/admin/profile/me');
        const next = updateStoredUser({
          fullName: data?.fullName || user?.fullName || "Admin",
          email: data?.email || user?.email || "",
          mobile: data?.mobile || user?.mobile || "",
          photo: data?.photo || user?.photo || "",
          photoUpdatedAt: Date.now(),
        });
        setUser(next);
      } catch (_err) {
        // keep existing session user
      }
    };
    syncAdminProfile();
    // run once on mount; admin profile page handles further updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    clearAdminPushSession()
      .catch(() => {})
      .finally(() => {
        clearStoredSession();
        redirectToLogin();
      });
  };

  const routeTitles = {
    "/admin": "Dashboard",
    "/admin/vendors": "Vendor Management",
    "/admin/vendors/:id/account": "Vendor Account",
    "/admin/vendor-security": "Vendor Security & Credit",
    "/admin/supplier-dispatches": "Supplier Dispatches",
    "/admin/suppliers": "Supplier Management",
    "/admin/suppliers/:id/account": "Supplier Account",
    "/admin/contractors": "Contractor Management",
    "/admin/contractors/:id/view": "Contractor Details",
    "/admin/contractors/:id/account": "Contractor Account",
    "/admin/contractor-rates": "Contractor Rates",
    "/admin/supplier-certificates": "Supplier Certificates",
    "/admin/employees": "Employee Management",
    "/admin/employees/:id/account": "Employee Account",
    "/admin/attendance-summary": "Attendance Summary",
    "/admin/attendance-live": "Live Tracking",
    "/admin/employee-tracking-history": "Employee Tracking History",
    "/admin/attendance-report": "Attendance",
    "/admin/vendor-requirements": "Requirements",
    "/admin/requirement-dispatches": "Requirement Dispatch",
    "/admin/reports": "Reports",
    "/admin/workflow-map": "Workflow Map",
    "/admin/meta-leads": "Meta Leads",
    "/admin/whatsapp": "WhatsApp Settings",
    "/admin/whatsapp/history": "WhatsApp History",
    "/admin/departments-roles": "Departments & Roles",
    "/admin/notifications": "Notifications",
    "/admin/payroll": "Payroll Approvals",
    "/admin/payouts": "Payout Desk",
    "/admin/payment-history": "Payment History",
    "/admin/payments/phonepe": "Receive Payment Link",
    "/admin/phonepe-transactions": "PhonePe Transactions",
    "/admin/products": "Products & Gallery",
    "/admin/certificates": "Issue Certificates",
    "/admin/idcards": "ID Cards",
     "/admin/profile": "My Profile",   
    
  };

  const currentPath = location.pathname;
  const vendorTabs = [
    { path: "/admin/vendors", label: "Vendor List" },
    { path: "/admin/vendor-security", label: "Security & Credit" },
    { path: "/admin/vendor-requirements", label: "Requirements" },
    { path: "/admin/requirement-dispatches", label: "Dispatch" },
  ];
  const supplierTabs = [
    { path: "/admin/suppliers", label: "Supplier List" },
    { path: "/admin/supplier-dispatches", label: "Dispatches" },
  ];
  const employeeTabs = [
    { path: "/admin/employees", label: "Employee List" },
    { path: "/admin/attendance-live", label: "Live Tracking" },
    { path: "/admin/employee-tracking-history", label: "Tracking History" },
    { path: "/admin/attendance-report", label: "Attendance" },
    { path: "/admin/worktime-summary", label: "Worktime" },
    { path: "/admin/departments-roles", label: "Departments & Roles" },
  ];
  const docsTabs = [
    { path: "/admin/certificates", label: "Vendor Certificates" },
    { path: "/admin/supplier-certificates", label: "Supplier Certificates" },
    { path: "/admin/idcards", label: "ID Cards" },
  ];
  const contractorTabs = [
    { path: "/admin/contractors", label: "Contractor List" },
    { path: "/admin/contractor-rates", label: "Contractor Rates" },
  ];
  const paymentTabs = [
    { path: "/admin/payouts", label: "Payout Desk" },
    { path: "/admin/payment-history", label: "Payment History" },
    { path: "/admin/payments/phonepe", label: "Receive Payment Link" },
    { path: "/admin/phonepe-transactions", label: "PhonePe Transactions" },
    { path: "/admin/payroll", label: "Payroll Approvals" },
  ];
  const whatsappTabs = [
    { path: "/admin/whatsapp", label: "WhatsApp Settings" },
    { path: "/admin/whatsapp/history", label: "Message History" },
  ];
  const reportsTabs = [
    { path: "/admin/reports", label: "Reports" },
    { path: "/admin/workflow-map", label: "Workflow Map" },
  ];
  const metaLeadsTabs = [{ path: "/admin/meta-leads", label: "Meta Leads" }];
  const sectionGroups = [
    { key: "vendors", tabs: vendorTabs, prefixes: vendorTabs.map((t) => t.path) },
    { key: "suppliers", tabs: supplierTabs, prefixes: supplierTabs.map((t) => t.path) },
    { key: "employees", tabs: employeeTabs, prefixes: employeeTabs.map((t) => t.path) },
    { key: "docs", tabs: docsTabs, prefixes: docsTabs.map((t) => t.path) },
    { key: "contractors", tabs: contractorTabs, prefixes: contractorTabs.map((t) => t.path) },
    { key: "payments", tabs: paymentTabs, prefixes: paymentTabs.map((t) => t.path) },
    { key: "whatsapp", tabs: whatsappTabs, prefixes: whatsappTabs.map((t) => t.path) },
    { key: "reports", tabs: reportsTabs, prefixes: reportsTabs.map((t) => t.path) },
    { key: "meta-leads", tabs: metaLeadsTabs, prefixes: metaLeadsTabs.map((t) => t.path) },
  ];
  const isAdminDashboardRoute = currentPath === "/admin" || currentPath === "/admin/";
  const matchedPath = Object.keys(routeTitles)
    .filter(path => matchesRoutePattern(path, currentPath))
    .sort((a, b) => b.length - a.length)[0];
  const currentTitle = matchedPath ? routeTitles[matchedPath] : "Dashboard";
  const isBottomActive = (prefixes = []) =>
    prefixes.some((p) => currentPath === p || currentPath.startsWith(`${p}/`));
  const isAdminDashboardBottomActive =
    currentPath === "/admin" || currentPath === "/admin/";
  const activeSection = sectionGroups.find((group) =>
    group.prefixes.some((p) => currentPath === p || currentPath.startsWith(`${p}/`))
  );
  const contextTabs = activeSection?.tabs || [];
  const isContextTabActive = (path) => currentPath === path || currentPath.startsWith(`${path}/`);
  const userName = user?.fullName || "Admin";
  const userInitials = useMemo(
    () =>
      String(userName)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() || "")
        .join("") || "A",
    [userName]
  );
  const userPhoto = resolveProfilePhoto(
    user?.photo,
    user?.photoUpdatedAt || user?.updatedAt || ""
  );

  useEffect(() => {
    if (!user?.id) return;
    initAdminPush(user.id).catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 768px)").matches) return;

    const root = document.querySelector(".admin-content");
    if (!root) return;

    const tables = root.querySelectorAll("table");
    tables.forEach((table) => {
      const headers = Array.from(table.querySelectorAll("thead th")).map((th, idx) => {
        const txt = String(th?.textContent || "").replace(/\s+/g, " ").trim();
        return txt || `Field ${idx + 1}`;
      });

      table.querySelectorAll("tbody tr").forEach((tr) => {
        Array.from(tr.children).forEach((cell, idx) => {
          if (!(cell instanceof HTMLElement)) return;
          const label = headers[idx] || `Field ${idx + 1}`;
          cell.setAttribute("data-label", label);
        });
      });
    });
  }, [location.pathname]);

  const openWhatsAppModal = async () => {
    setShowWhatsAppModal(true);
    setWaError("");
    setWaForm((prev) => ({ ...prev, templateId: "" }));
    try {
      setWaLoading(true);
      const { data } = await API.get("/admin/whatsapp/templates");
      const templates = Array.isArray(data?.templates) ? data.templates : [];
      setWaTemplates(templates);
      if (templates.length) {
        const selected = templates.find((item) => item.isDefault) || templates[0];
        setWaForm((prev) => ({ ...prev, templateId: selected?.templateId || "" }));
      }
    } catch (e) {
      setWaTemplates([]);
      setWaError(e?.response?.data?.message || "Failed to load templates");
    } finally {
      setWaLoading(false);
    }
  };

  const sendWhatsAppFromLayout = async () => {
    if (!String(waForm.to || "").trim()) {
      setWaError("Recipient number is required");
      return;
    }
    if (!String(waForm.templateId || "").trim()) {
      setWaError("Please select a template");
      return;
    }
    try {
      setWaSending(true);
      setWaError("");
      await API.post("/admin/whatsapp/send", {
        to: waForm.to,
        recipientName: waForm.recipientName || "User",
        templateId: waForm.templateId,
        sourceModule: "admin_global_whatsapp",
        eventType: "manual_global_message",
      });
      setShowWhatsAppModal(false);
      setWaForm({ to: "", recipientName: "", templateId: "" });
    } catch (e) {
      setWaError(e?.response?.data?.message || "Failed to send WhatsApp");
    } finally {
      setWaSending(false);
    }
  };

  return (
    <div
      className="admin-main-scroll panel-layout-shell"
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        '--admin-sidebar-gradient': 'linear-gradient(180deg, #0b3b8f 0%, #1d4ed8 100%)',
        '--admin-sidebar-border': '#082c67',
        '--admin-title-color': '#2563eb',
        '--admin-nav-bg': '#0b3b8f',
        '--admin-nav-border': 'rgba(255, 255, 255, 0.18)',
        '--admin-nav-shadow': '0 -10px 30px rgba(11, 59, 143, 0.35)',
        '--admin-nav-active-bg': '#ffffff',
        '--admin-nav-active-color': '#0b3b8f',
        '--admin-nav-active-shadow': '0 2px 8px rgba(11, 59, 143, 0.24)',
      }}
    >
      {/* Sidebar for desktop */}
      <div className="d-none d-md-block" style={{ width: '220px', background: 'var(--admin-sidebar-gradient)', height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            color: 'white',
            fontSize: '22px',
            fontWeight: 'bold',
            padding: '14.5px',
            borderBottom: '1px solid var(--admin-sidebar-border)',
            textAlign: 'center',
          }}
        >
          🌿 LOTUS RICE MILL PVT. LTD.
        </div>
        <div className="admin-sidebar-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <AdminSidebar eligible={eligible} />
        </div>
      </div>

      {/* Sidebar Offcanvas for mobile */}
      <Offcanvas
        show={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="admin-mobile-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <span className="material-symbols-outlined">eco</span>
            <span>Lotus Rice Mill Pvt. Ltd. Admin</span>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="admin-sidebar-scroll" style={{ background: 'var(--admin-sidebar-gradient)', color: '#fff' }}>
          <AdminSidebar
            eligible={eligible}
            onNavigate={() => setSidebarVisible(false)}
          />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main content */}
      <div className="admin-main-col panel-layout-maincol" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Topbar */}
        <div
          className="d-flex justify-content-between align-items-center p-2 p-md-3 panel-layout-topbar"
          style={{ backgroundColor: 'var(--app-topbar)', borderBottom: '1px solid #dee2e6' }}
        >
          <div className="d-flex align-items-center gap-2 admin-topbar-left">
            {/* Mobile menu button */}
            {!isAdminDashboardRoute && (
              <Button
                variant="light"
                className="d-md-none"
                onClick={handleGoBack}
                aria-label="Go back"
              >
                <span className="material-symbols-outlined">arrow_back_ios_new</span>
              </Button>
            )}
            <Button
              variant="light"
              className="d-md-none"
              onClick={() => setSidebarVisible(true)}
            >
              <BsList />
            </Button>

            <div className="admin-topbar-title" style={{ fontSize: '18px', fontWeight: 600, color: 'var(--admin-title-color)' }}>
              {currentTitle}
            </div>
          </div>

          <div className="d-flex align-items-center gap-3 position-relative">
            <div
              style={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => navigate('/admin/notifications')}
            >
              <BsBellFill size={20} className="text-secondary" />
              {notificationCount > 0 && (
                <Badge
                  bg="danger"
                  pill
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-10px',
                    fontSize: '10px',
                  }}
                >
                  {notificationCount}
                </Badge>
              )}
            </div>

            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                style={{ fontWeight: "bold", display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                {userPhoto ? (
                  <img
                    src={userPhoto}
                    alt={userName}
                    style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }}
                  />
                ) : (
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#e2e8f0",
                      color: "#334155",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {userInitials}
                  </span>
                )}
                <span className="admin-user-name">{userName}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                 <Dropdown.Item onClick={() => navigate('/admin/profile')}>
                  Profile
                </Dropdown.Item>
                <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="admin-gst-api-alert" role="status">
          <span className="material-symbols-outlined">verified</span>
          <div>
            <strong>{GST_API_LIFETIME_MESSAGE}</strong>
            <span> GST lookups are active for all portals.</span>
          </div>
        </div>

        {contextTabs.length > 0 ? (
          <div className="admin-context-tabs-wrap">
            <div className="admin-context-tabs">
              {contextTabs.map((tab) => (
                <button
                  key={tab.path}
                  type="button"
                  className={`admin-context-tab ${isContextTabActive(tab.path) ? "active" : ""}`}
                  onClick={() => navigate(tab.path)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Content */}
        <div className="admin-content admin-content-scroll panel-layout-content" style={{ flex: 1, overflowY: 'auto', background: 'var(--app-bg)', padding: '15px', minHeight: 0 }}>
          <Outlet />
        </div>

        <button
          type="button"
          className="admin-global-whatsapp-btn"
          onClick={openWhatsAppModal}
          title="WhatsApp"
          aria-label="Open WhatsApp panel"
        >
          <span className="material-symbols-outlined">forum</span>
          <span>WhatsApp</span>
        </button>

        <Modal show={showWhatsAppModal} onHide={() => setShowWhatsAppModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Send WhatsApp</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {waError ? <div className="alert alert-danger py-2">{waError}</div> : null}
            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Mobile Number</Form.Label>
              <Form.Control
                placeholder="+91xxxxxxxxxx"
                value={waForm.to}
                onChange={(e) => setWaForm((prev) => ({ ...prev, to: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="mb-1">Recipient Name (optional)</Form.Label>
              <Form.Control
                placeholder="Name"
                value={waForm.recipientName}
                onChange={(e) => setWaForm((prev) => ({ ...prev, recipientName: e.target.value }))}
              />
            </Form.Group>

            <div className="admin-wa-template-head">Select Template</div>
            {waLoading ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : waTemplates.length === 0 ? (
              <div className="text-muted small">No templates configured. Add templates in WhatsApp Settings.</div>
            ) : (
              <div className="admin-wa-template-list">
                {waTemplates.map((t) => (
                  <button
                    key={t.templateId}
                    type="button"
                    className={`admin-wa-template-item ${waForm.templateId === t.templateId ? "active" : ""}`}
                    onClick={() => setWaForm((prev) => ({ ...prev, templateId: t.templateId }))}
                  >
                    <span className="admin-wa-template-name">{t.label || t.name || t.templateId}</span>
                    <span className="admin-wa-template-id">{t.templateId}</span>
                  </button>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowWhatsAppModal(false)}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={sendWhatsAppFromLayout}
              disabled={waSending || waLoading || !waTemplates.length}
            >
              {waSending ? "Sending..." : "Send"}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showGstApiDuePopup}
          onHide={() => setShowGstApiDuePopup(false)}
          centered
          className="admin-gst-api-due-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>GST API Activation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="admin-gst-api-due-popup">
              <span className="material-symbols-outlined">verified</span>
              <div>
                <h5>{GST_API_LIFETIME_MESSAGE}</h5>
                <p>GST API access is active and available from the provider side.</p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="success" onClick={() => setShowGstApiDuePopup(false)}>
              Acknowledge
            </Button>
          </Modal.Footer>
        </Modal>

        <div className="admin-bottom-nav">
          <button
            className={`admin-bottom-item ${isAdminDashboardBottomActive ? 'active' : ''}`}
            onClick={() => navigate('/admin')}
          >
            <span className="material-symbols-outlined">dashboard</span>
            <span>Dashboard</span>
          </button>
          <button
            className={`admin-bottom-item ${isBottomActive(['/admin/vendors', '/admin/vendor-security', '/admin/vendor-requirements', '/admin/requirement-dispatches']) ? 'active' : ''}`}
            onClick={() => navigate('/admin/vendors')}
          >
            <span className="material-symbols-outlined">group</span>
            <span>Vendors</span>
          </button>
          <button
            className={`admin-bottom-item ${isBottomActive(['/admin/suppliers', '/admin/contractors', '/admin/contractor-rates', '/admin/supplier-dispatches']) ? 'active' : ''}`}
            onClick={() => navigate('/admin/suppliers')}
          >
            <span className="material-symbols-outlined">local_shipping</span>
            <span>Suppliers</span>
          </button>
          <button
            className={`admin-bottom-item ${isBottomActive(['/admin/employees', '/admin/attendance-live', '/admin/employee-tracking-history', '/admin/attendance-report', '/admin/worktime-summary', '/admin/departments-roles', '/admin/payroll', '/admin/payouts', '/admin/payment-history', '/admin/payments/phonepe', '/admin/phonepe-transactions']) ? 'active' : ''}`}
            onClick={() => navigate('/admin/employees')}
          >
            <span className="material-symbols-outlined">badge</span>
            <span>Employees</span>
          </button>
          <button
            className={`admin-bottom-item ${isBottomActive(['/admin/profile']) ? 'active' : ''}`}
            onClick={() => navigate('/admin/profile')}
          >
            <span className="material-symbols-outlined">person</span>
            <span>Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminMainLayout;
